import {
  IonButton,
  IonItem,
  IonLabel,
  IonList,
  IonRadio,
  IonRadioGroup,
  IonSpinner,
  IonText,
  IonTextarea,
} from "@ionic/react";
import { usePanelFlow, type PanelConfig } from "components/PanelFlow";
import { isReasonDissatisfied, isReasonOther } from "model";
// FIXME: Change this to reference `Icon` from `swing-components`
import { MdArrowForward } from "react-icons/md";
import { Alert, Asterisk } from "swing-components";
import { generateSwingSupportURL } from "swing-utils";

import { translate } from "~utils";

import styles from "./CancelRequestModal.module.css";
import { CancelPanelKey, CancelReasonKey } from "./constants";
import type { CancelRequestLowLevelPanelProps } from "./types";
import { getReasonConfigByKey, reasonConfigs } from "./utils";

export function CancelReason({
  state,
  goToPanel,
  setState,
  extraProps,
}: CancelRequestLowLevelPanelProps) {
  const { request, onDismiss } = extraProps;
  const showDissatisfiedOption =
    request?.isMultiDayRequest() && request.isFilled() && request.isStarted();
  const reasonOptions = showDissatisfiedOption
    ? reasonConfigs
    : reasonConfigs.filter((config) => config.reasonKey !== CancelReasonKey.Dissatisfied);

  return (
    <IonList className={styles.listFill}>
      <IonItem lines="none">
        <IonText>
          {translate("CANCEL_REQ_LABEL_WHY_CANCEL")}
          <Asterisk />
        </IonText>
      </IonItem>
      <IonRadioGroup value={state.cancelReason}>
        {reasonOptions.map(({ reasonKey, reasonText }) => {
          return (
            <IonItem
              key={reasonKey}
              onClick={() =>
                setState((currentState) => ({
                  ...currentState,
                  cancelReason: reasonKey,
                }))
              }
            >
              <IonLabel>{reasonText}</IonLabel>
              <IonRadio mode="md" slot="start" value={reasonKey} aria-label={reasonKey} />
            </IonItem>
          );
        })}
      </IonRadioGroup>
      <div className={styles.pairButtonHolder}>
        <IonButton
          color="primary"
          className="w-full ion-no-margin"
          disabled={!state.cancelReason}
          onClick={() => goToPanel(CancelPanelKey.CancelConfirmPanel)}
        >
          {translate("LABEL_NEXT")}
          <MdArrowForward />
        </IonButton>
        <IonButton
          color="primary"
          fill="outline"
          className="w-full ion-no-margin"
          onClick={() => onDismiss(null, "cancel")}
        >
          {translate("CANCEL_REQ_BUTTON_DO_NOT_CANCEL")}
        </IonButton>
      </div>
    </IonList>
  );
}

export const CancelConfirm: React.FC<CancelRequestLowLevelPanelProps> = ({
  state,
  goToPanel,
  setState,
  extraProps,
}) => {
  const { request, onDismiss, onSubmit, isSubmitting, setIsSubmitting } = extraProps;
  const reasonConfig = getReasonConfigByKey(state.cancelReason, reasonConfigs);
  const isCancelReasonOther = isReasonOther(state.cancelReason);
  const isCancelReasonDissatisfied = isReasonDissatisfied(state.cancelReason);
  const onCancel = async () => {
    setIsSubmitting(true);

    const result = await onSubmit(state);

    if (!result.isSuccess) {
      return goToPanel(CancelPanelKey.CancelErrorPanel);
    }
    return goToPanel(CancelPanelKey.CancelSuccessPanel);
  };

  return (
    <IonList className={styles.listFill}>
      {!isCancelReasonDissatisfied && request?.willStillBeInvoiced && (
        <IonItem lines="none">
          <Alert color="danger">
            {translate("CANCEL_REQ_LABEL_LAST_MIN_WARNING")}{" "}
            <a
              target="_blank"
              rel="noreferrer"
              href={generateSwingSupportURL(
                "/hc/en-us/articles/115005318228-What-is-the-Policy-for-Cancellations",
              )}
            >
              {translate("CANCEL_REQ_LABEL_CANCELLATION_POLICY")}
            </a>
            .
          </Alert>
        </IonItem>
      )}

      <IonItem lines="none">
        <IonText className="block">
          {isCancelReasonDissatisfied ? (
            translate("CANCEL_REQ_LABEL_APOLOGY")
          ) : (
            <>
              <span className="font-medium">{translate("CANCEL_REQ_LABEL_REASON_SELECTED")}</span>{" "}
              {reasonConfig?.reasonText ||
                translate("LABEL_INTERNAL_ERROR", {
                  errorCode: "Error Code: NO_CANCEL_REASON_TEXT",
                })}
            </>
          )}
        </IonText>
      </IonItem>

      {(isCancelReasonOther || isCancelReasonDissatisfied) && (
        <IonItem>
          <IonLabel position="stacked" className="font-medium">
            {isCancelReasonDissatisfied
              ? translate("CANCEL_REQ_LABEL_WHY_DISSATISFIED")
              : translate("CANCEL_REQ_LABEL_WHAT_OTHER_REASON")}
            <Asterisk />
          </IonLabel>
          <IonTextarea
            mode="md"
            data-testid="cancel_request_modal-other_reason_field"
            placeholder={
              isCancelReasonDissatisfied
                ? translate("CANCEL_REQ_LABEL_ADD_FEEDBACK")
                : translate("CANCEL_REQ_LABEL_ADD_EXPLANATION")
            }
            rows={4}
            autofocus
            onIonInput={(e) =>
              setState((currentState) => ({
                ...currentState,
                cancelExplanation: e.target.value as string,
              }))
            }
          />
        </IonItem>
      )}

      <IonItem lines="none">
        <IonLabel>
          <IonText className="block font-medium mb-2">
            {translate("CANCEL_REQ_LABEL_CONFIRM_CANCEL")}
          </IonText>
          <IonText>
            {isCancelReasonDissatisfied
              ? translate("CANCEL_REQ_LABEL_NO_CHARGE")
              : translate("CANCEL_REQ_LABEL_GENERAL_WARNING")}
          </IonText>
        </IonLabel>
      </IonItem>

      <div className={styles.pairButtonHolder}>
        <IonButton
          color="primary"
          className="w-full ion-no-margin"
          disabled={
            ((isCancelReasonOther || isCancelReasonDissatisfied) && !state.cancelExplanation) ||
            isSubmitting
          }
          onClick={onCancel}
        >
          {isSubmitting
            ? translate("CANCEL_REQ_BUTTON_PROCESSING")
            : translate("CANCEL_REQ_BUTTON_CONFIRM_CANCEL")}
          {isSubmitting && <IonSpinner name="dots" />}
        </IonButton>
        <IonButton
          color="primary"
          fill="outline"
          className="w-full ion-no-margin"
          disabled={isSubmitting}
          onClick={() => onDismiss(null, "cancel")}
        >
          {translate("CANCEL_REQ_BUTTON_CONFIRM_DO_NOT_CANCEL")}
        </IonButton>
      </div>
    </IonList>
  );
};

export const CancelSuccess: React.FC<CancelRequestLowLevelPanelProps> = ({ state, extraProps }) => {
  const { onDismiss } = extraProps;

  return (
    <IonList className={styles.listFill}>
      <IonItem lines="none">
        {isReasonDissatisfied(state.cancelReason)
          ? translate("CANCEL_REQ_LABEL_CANCELLED_WITH_FEEDBACK")
          : translate("CANCEL_REQ_LABEL_CANCELLED")}
      </IonItem>
      <div className={styles.pairButtonHolder}>
        <IonButton
          color="primary"
          className="ion-no-margin w-full"
          onClick={() => onDismiss(null, "cancel")}
        >
          {translate("LABEL_CLOSE")}
        </IonButton>
      </div>
    </IonList>
  );
};

export const CancelError: React.FC<CancelRequestLowLevelPanelProps> = ({ extraProps }) => {
  const { onDismiss } = extraProps;

  return (
    <>
      <div className="mb-4">
        {translate("LABEL_INTERNAL_ERROR", {
          errorCode: "Error Code: CANCEL_REQ_API_ERROR",
        })}
      </div>
      <IonButton
        color="primary"
        className="ion-no-margin"
        onClick={() => onDismiss(null, "cancel")}
      >
        Close
      </IonButton>
    </>
  );
};

const CancelReasonPanel: React.FC = () => {
  return <CancelReason {...usePanelFlow()} />;
};

const CancelConfirmPanel: React.FC = () => {
  return <CancelConfirm {...usePanelFlow()} />;
};

const CancelSuccessPanel: React.FC = () => {
  return <CancelSuccess {...usePanelFlow()} />;
};

const CancelErrorPanel: React.FC = () => {
  return <CancelError {...usePanelFlow()} />;
};

export const panels: PanelConfig[] = [
  { key: CancelPanelKey.CancelReasonPanel, panel: CancelReasonPanel },
  { key: CancelPanelKey.CancelConfirmPanel, panel: CancelConfirmPanel },
  { key: CancelPanelKey.CancelSuccessPanel, panel: CancelSuccessPanel },
  { key: CancelPanelKey.CancelErrorPanel, panel: CancelErrorPanel },
];
